import React, { FunctionComponent, useEffect } from "react";
import ReactDOM from "react-dom";
import { MDBCol, MDBRow } from "mdbreact";
import Skeleton from "react-loading-skeleton";
import ProgressButton from "presentation/components/ProgressButton";
import WalletQRCodeModal from "presentation/components/WalletQRCodeModal";
import AlertModal from "presentation/components/AlertModal";
import { useAddToWallet } from "core/hooks/useAddToWallet";
import { useWalletAnalytics } from "core/hooks/useWalletAnalytics";
import { WalletPassParams } from "domain/entity/WalletPass";

interface WalletCTAProps {
  params?: WalletPassParams;
}

const WalletCTA: FunctionComponent<WalletCTAProps> = ({ params }) => {
  const wallet = useAddToWallet();
  const analytics = useWalletAnalytics();

  useEffect(() => {
    if (wallet.isEnabled && params) {
      analytics.trackWalletCtaViewed({ planType: params.productType });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet.isEnabled, params?.productType]);

  if (wallet.isLoadingFlag) {
    return (
      <MDBRow>
        <MDBCol>
          <Skeleton height="3.1rem" className="mb-3" />
        </MDBCol>
      </MDBRow>
    );
  }

  if (!wallet.isEnabled) {
    return null;
  }

  if (!params) {
    return null;
  }

  const handleAppleClick = () => wallet.addToAppleWallet(params);
  const handleGoogleClick = () => wallet.addToGoogleWallet(params);

  return (
    <>
      <MDBRow>
        <MDBCol>
          <ProgressButton
            color="default"
            block
            className="btn-md font-weight-semi-bold text-capitalize mb-2 px-3"
            text="Add to Apple Wallet"
            loading={wallet.isLoadingApple}
            onClick={handleAppleClick}
            id="add-to-apple-wallet"
          />
        </MDBCol>
      </MDBRow>
      <MDBRow>
        <MDBCol>
          <ProgressButton
            color="default"
            block
            className="btn-md font-weight-semi-bold text-capitalize mb-3 px-3"
            text="Add to Google Wallet"
            loading={wallet.isLoadingGoogle}
            onClick={handleGoogleClick}
            id="add-to-google-wallet"
          />
        </MDBCol>
      </MDBRow>

      {!wallet.isMobile &&
        wallet.appleQRUrl &&
        ReactDOM.createPortal(
          <WalletQRCodeModal
            isOpen={wallet.isAppleModalOpen}
            title="Add to Apple Wallet"
            url={wallet.appleQRUrl}
            onClose={wallet.closeAppleModal}
          />,
          document.body
        )}

      {!wallet.isMobile &&
        wallet.googleQRUrl &&
        ReactDOM.createPortal(
          <WalletQRCodeModal
            isOpen={wallet.isGoogleModalOpen}
            title="Add to Google Wallet"
            url={wallet.googleQRUrl}
            onClose={wallet.closeGoogleModal}
          />,
          document.body
        )}

      {ReactDOM.createPortal(
        <AlertModal
          isOpen={!!wallet.errorMessage}
          title="Add to Wallet"
          body={wallet.errorMessage ?? undefined}
          positive="OK"
          onPositive={wallet.clearError}
          onDismiss={wallet.clearError}
        />,
        document.body
      )}
    </>
  );
};

export default WalletCTA;
